/**
 * Status koneksi untuk banner "sinyal lemah" (Okt 2026, Fase 4C).
 *
 * Tidak menambah request apa pun. Hanya mengamati request axios yang sudah ada:
 *  - request gagal tanpa respons / timeout  -> sinyal lemah
 *  - request biasa (bukan upload) sangat lambat -> sinyal lemah
 *  - request biasa cepat berhasil -> sinyal normal lagi
 *  - navigator.onLine === false -> offline
 * Upload file dan unduhan blob diabaikan, karena memang wajar lambat.
 */

import axios from 'axios';

const SLOW_MS = 8000; // respons lebih lambat dari ini dianggap sinyal lemah
const FAST_MS = 3000; // respons secepat ini dianggap sinyal sudah normal
const WEAK_HOLD_MS = 25000; // bendera lemah kedaluwarsa kalau tidak ada bukti baru

let weakUntil = 0;
let online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
let expireTimer = null;
const listeners = new Set();
let installed = null;

function snapshot() {
  if (!online) return 'offline';
  return Date.now() < weakUntil ? 'weak' : 'ok';
}

let last = snapshot();

function recompute() {
  const next = snapshot();
  if (expireTimer) {
    clearTimeout(expireTimer);
    expireTimer = null;
  }
  if (next === 'weak') {
    expireTimer = setTimeout(recompute, Math.max(500, weakUntil - Date.now() + 50));
  }
  if (next !== last) {
    last = next;
    listeners.forEach((fn) => {
      try { fn(next); } catch { /* abaikan */ }
    });
  }
}

export function getConnectionStatus() {
  return last;
}

export function subscribeConnection(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function markWeak() {
  weakUntil = Date.now() + WEAK_HOLD_MS;
  recompute();
}

function markGood() {
  if (weakUntil === 0) return;
  weakUntil = 0;
  recompute();
}

const isBulky = (config) => {
  if (!config) return false;
  if (config.responseType && config.responseType !== 'json') return true;
  return typeof FormData !== 'undefined' && config.data instanceof FormData;
};

/** Pasang pengamat sekali. Mengembalikan fungsi pelepas. */
export function installConnectionMonitor() {
  if (installed) return installed.cleanup;

  const onOnline = () => { online = true; recompute(); };
  const onOffline = () => { online = false; recompute(); };
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  online = navigator.onLine !== false;
  recompute();

  const reqId = axios.interceptors.request.use((config) => {
    config.__vmT0 = Date.now();
    return config;
  });
  const resId = axios.interceptors.response.use(
    (response) => {
      const cfg = response?.config;
      if (cfg && !isBulky(cfg) && cfg.__vmT0 && !cfg.__vmFromCache) {
        const ms = Date.now() - cfg.__vmT0;
        if (ms >= SLOW_MS) markWeak();
        else if (ms <= FAST_MS) markGood();
      }
      return response;
    },
    (error) => {
      try {
        if (axios.isAxiosError(error) && !axios.isCancel(error) && error.code !== 'ERR_CANCELED') {
          if (!error.response && !isBulky(error.config)) markWeak();
          else if (error.response && !isBulky(error.config) && error.config?.__vmT0
            && Date.now() - error.config.__vmT0 <= FAST_MS) markGood();
        }
      } catch { /* pengamat tidak boleh mengubah hasil request */ }
      return Promise.reject(error);
    }
  );

  installed = {
    cleanup: () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(resId);
      if (expireTimer) clearTimeout(expireTimer);
      expireTimer = null;
      installed = null;
    },
  };
  return installed.cleanup;
}
